import axios from 'axios';
import { toast } from 'react-hot-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Applies a single item-field change, performing SAP auto-population side
 * effects (material details → short text / UoM / plant data, etc.) and
 * total-value recalculation. Mutates a cloned `items` array and returns it.
 *
 * @param items current items array (will be cloned by caller)
 * @param index active item index
 * @param field field name
 * @param value new value
 * @param setMaterialPlants setter to stash a material's plant list for filtering
 * @param opts { autoPopulateValuation } — Create auto-fills valuation/MRP from
 *        material master; Edit does not.
 */
export async function applyItemChange(items, index, field, value, setMaterialPlants, opts = {}) {
  const { autoPopulateValuation = false } = opts;
  const newItems = [...items];
  newItems[index] = { ...newItems[index], [field]: value };

  if (field === 'material_id' && value) {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/materials/${value}/details`);
      const materialData = response.data;

      if (materialData.description) newItems[index].short_text = materialData.description;
      if (materialData.base_uom) {
        newItems[index].unit_of_measure = materialData.base_uom;
        newItems[index].per_unit_uom = materialData.base_uom;
      }
      if (materialData.material_group) newItems[index].material_group = materialData.material_group;

      if (autoPopulateValuation) {
        if (materialData.valuation_type) newItems[index].valuation_type = materialData.valuation_type;
        if (materialData.valuation_price) {
          newItems[index].valuation_price = String(materialData.valuation_price);
          newItems[index].estimated_unit_price = String(materialData.valuation_price);
        }
        if (materialData.valuation_currency) {
          newItems[index].currency = materialData.valuation_currency;
          newItems[index].price_currency = materialData.valuation_currency;
        }
        if (materialData.mrp_controller) newItems[index].mrp_controller = materialData.mrp_controller;
      }

      setMaterialPlants((prev) => ({ ...prev, [index]: materialData.plants || [] }));

      if (materialData.plants && materialData.plants.length > 0) {
        if (materialData.plants.length === 1) {
          const plantData = materialData.plants[0];
          newItems[index].plant = plantData.plant_id;
          if (plantData.storage_location) newItems[index].storage_location = plantData.storage_location;
          if (plantData.cost_center) newItems[index].cost_center = plantData.cost_center;
          if (plantData.purchasing_organization) newItems[index].purchasing_organization = plantData.purchasing_organization;
          if (plantData.purchasing_group) newItems[index].purchasing_group = plantData.purchasing_group;
          if (plantData.mrp_controller) newItems[index].mrp_controller = plantData.mrp_controller;
          if (plantData.abc_indicator) newItems[index].abc_indicator = plantData.abc_indicator;
          toast.success(`Material loaded! Plant ${plantData.plant_id} auto-selected.`);
        } else {
          toast.success(`Material ${value} has ${materialData.plants.length} plants. Please select plant manually.`, { duration: 4000 });
        }
      } else {
        toast.error(`Material ${value} has no plant extensions. Please select plant and other fields manually.`);
      }
    } catch (error) {
      console.error('Error fetching material details:', error);
      if (error.response?.status === 404) toast.error(`Material ${value} not found in system`);
      else toast.error(`Error loading material details: ${error.message}`);
    }
  }

  if (field === 'plant' && value && newItems[index].material_id) {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/materials/${newItems[index].material_id}/details`);
      const materialData = response.data;
      const plantData = materialData.plants?.find((p) => p.plant_id === value);
      if (plantData) {
        if (plantData.storage_location && !newItems[index].storage_location) newItems[index].storage_location = plantData.storage_location;
        if (plantData.cost_center && !newItems[index].cost_center) newItems[index].cost_center = plantData.cost_center;
        if (plantData.purchasing_organization && !newItems[index].purchasing_organization) newItems[index].purchasing_organization = plantData.purchasing_organization;
        if (plantData.purchasing_group && !newItems[index].purchasing_group) newItems[index].purchasing_group = plantData.purchasing_group;
        if (plantData.mrp_controller) newItems[index].mrp_controller = plantData.mrp_controller;
        if (plantData.abc_indicator) newItems[index].abc_indicator = plantData.abc_indicator;
        toast.success(`Plant ${value} data loaded!`);
      }
    } catch (error) {
      console.error('Error fetching plant data:', error);
    }
  }

  if ((field === 'valuation_price' || field === 'quantity') && newItems[index].valuation_price && newItems[index].quantity) {
    newItems[index].total_value = (parseFloat(newItems[index].valuation_price) * parseFloat(newItems[index].quantity)).toFixed(2);
  }

  return newItems;
}
